import { segmentKind } from "@/lib/aggregateSegments";
import { formatBondCurrency } from "@/lib/bondValueDisplay";
import {
  CAPACITY_REQUEST_DOCUMENT_TYPE,
  type CapacityProject,
  type CapacityRequestInput,
} from "@/lib/capacityRequest";

// BRK-S404 — the AIA G702/G703 pay application, filed from a performance and
// payment row of the account's aggregate bar as a `capacity_wip` capacity
// request with the G702 figures beside its document (PMS-S849, ahead of the
// pin).
//
// HOW MUCH CAPACITY COMES BACK IS PMS'S RULE, NOT OURS. The wireframe credited
// back half of the completed work in the browser; nothing here computes a
// percentage or a release. The estimate is shown only when PMS serves
// `estimatedRelease`, and the underwriter sets the final amount (NN #7).

/** The pay application is the WIP route's document, so it files that route's
 *  one served type — no list of types lives here. */
export const PAY_APPLICATION_DOCUMENT_TYPE =
  CAPACITY_REQUEST_DOCUMENT_TYPE.capacity_wip;

/**
 * The bonds a pay application can be filed for: performance and payment holds
 * whose capacity is LOCKED (the bond is in force). A bid has nothing billed,
 * and a basis this build has not adopted is never guessed at.
 */
export function payApplicationProjects(
  projects: readonly CapacityProject[],
): CapacityProject[] {
  return projects.filter(
    (p) => segmentKind(p.basis) === "pp" && p.state === "locked",
  );
}

/**
 * Why the dialog has no bond to offer, or null when it has one (BRK-S404). A
 * P&P still being issued holds capacity OPEN, and saying "bid bonds" about it
 * (SB-00001) told the broker nothing; that sentence is kept for an account
 * holding bid bonds only.
 */
export function payApplicationEmptyMessage(
  projects: readonly CapacityProject[],
): string | null {
  if (payApplicationProjects(projects).length > 0) return null;
  const issuing = projects
    .filter((p) => segmentKind(p.basis) === "pp" && p.state === "open")
    .map((p) => p.orderNumber);
  if (issuing.length === 0) {
    return "Bid bonds don't need a pay application. Report the bid result from the aggregate bar instead.";
  }
  const verb = issuing.length === 1 ? "is" : "are";
  return `A pay application needs a performance and payment bond in force. ${issuing.join(", ")}'s ${verb} still being issued.`;
}

export interface PayApplicationDraft {
  /** The order's pointer id; "" until one is chosen. */
  bondSubmissionId: string;
  /** Documents PMS confirmed with a 201 — never a picked-but-unsent file. */
  documents: readonly { id: string }[];
  applicationNumber: number | undefined;
  /** The date input's value: ISO YYYY-MM-DD, or "" while empty. */
  periodTo: string;
  contractSum: number | undefined;
  completedToDate: number | undefined;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * What still stands between the draft and Submit, in field order. Required
 * fields only: whether completed may exceed the contract sum is PMS's to
 * refuse (its 422 renders inline), not a rule kept twice.
 */
export function payApplicationReasons(draft: PayApplicationDraft): string[] {
  const reasons: string[] = [];
  if (draft.bondSubmissionId === "") reasons.push("Choose the bond.");
  if (draft.documents.length === 0) {
    reasons.push("Upload the pay application.");
  }
  if (draft.applicationNumber === undefined) {
    reasons.push("Enter the application number.");
  } else if (
    !Number.isInteger(draft.applicationNumber) ||
    draft.applicationNumber < 1
  ) {
    reasons.push("Enter the application number as a whole number.");
  }
  if (!ISO_DATE.test(draft.periodTo)) reasons.push("Enter the period to date.");
  if (!(draft.contractSum !== undefined && draft.contractSum > 0)) {
    reasons.push("Enter the contract sum to date.");
  }
  if (!(draft.completedToDate !== undefined && draft.completedToDate > 0)) {
    reasons.push("Enter the amount completed and stored to date.");
  }
  return reasons;
}

/** The request body: plain numbers and an ISO date on the wire (D-10). Call
 *  only once `payApplicationReasons` is empty. */
export function payApplicationInput(
  draft: PayApplicationDraft,
): CapacityRequestInput {
  return {
    kind: "capacity_wip",
    bondSubmissionId: draft.bondSubmissionId,
    documentIds: draft.documents.map((d) => d.id),
    applicationNumber: draft.applicationNumber,
    periodTo: draft.periodTo,
    contractSum: draft.contractSum,
    completedToDate: draft.completedToDate,
  };
}

/** PMS's estimate, worded — or null when it served none. */
export function estimatedReleaseLine(
  estimatedRelease: number | null | undefined,
): string | null {
  if (typeof estimatedRelease !== "number") return null;
  return `Estimated capacity back: ${formatBondCurrency(estimatedRelease)} · the underwriter sets the final amount`;
}
